import React, { useMemo } from 'react';
import block from 'bem-cn';
import { useSelector, shallowEqual } from 'react-redux';
import dayjs from 'dayjs';

import Spinner from 'components/Spinner/desktop';
import { LineChart } from 'components/Charts/view/desktop/LineChart';
import SVG from 'components/SVG';

import useModel from 'features/statistic/model/useModel';

import usersSVG from './img/users.svg';
import totalBetsSVG from './img/totalBets.svg';
import totalNetwinSVG from './img/totalNetwin.svg';
import totalWinSVG from './img/totalWin.svg';
import activeUserSVG from './img/active-user.svg';
import newUserSVG from './img/new-user.svg';
import referralsSVG from './img/referrals.svg';

import Filters from './Filters';

import './Statistic.scss';
import Table from './Table';

const b = block('statistic-desktop');

const Statistic = () => {
  const model = useModel();

  const { actionProcessing, statistic } = useSelector(state => state.statistic, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);

  const switchTab = tabName => {
    model.inputState.setActiveTab(tabName);
  };

  const data = useMemo(() => {
    return {
      type: 'totalSum',
      data: statistic.netwinByDates.map(t => t.netwin),
      time: statistic.netwinByDates.map(t => dayjs(t.date).subtract(3, 'hour').format('DD.MM')),
      color: 'rgba(234, 220, 97, 1)',
      colorFill: 'rgb(169, 172, 77, 0.3)',
      title: 'Total',
    };
  }, [statistic.netwinByDates]);

  const activeTab = useMemo(() => model.inputState.activeTab, [model.inputState.activeTab]);

  return (
    <div className={b()}>
      <Spinner isLoading={actionProcessing} />
      <div className={b('title')}>{locale.myStatistic}</div>
      <div className={b('top')}>
        <div className={b('top-left')}>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.usersCount}</div>
              <div className={b('top-left-item-12')}>{locale.users}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: usersSVG }} />
          </div>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.newUsers}</div>
              <div className={b('top-left-item-12')}>{locale.newUsers}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: newUserSVG }} />
          </div>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.activeUser}</div>
              <div className={b('top-left-item-12')}>{locale.activeNewUsers}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: activeUserSVG }} />
          </div>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.totalBets}</div>
              <div className={b('top-left-item-12')}>{locale.totalBets}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: totalBetsSVG }} />
          </div>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.totalWin}</div>
              <div className={b('top-left-item-12')}>{locale.totalWin}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: totalWinSVG }} />
          </div>
          <div className={b('top-left-item')}>
            <div className={b('top-left-item-1')}>
              <div className={b('top-left-item-11')}>{statistic.totalNetwin}</div>
              <div className={b('top-left-item-12')}>{locale.totalNetwin}</div>
            </div>
            <SVG className={b('top-left-item-icon')} svgProps={{ svg: totalNetwinSVG }} />
          </div>
          {!model.outputState.ifInviteHidden && (
            <div className={b('top-left-item')}>
              <div className={b('top-left-item-1')}>
                <div className={b('top-left-item-11')}>{statistic.referrals}</div>
                <div className={b('top-left-item-12')}>{locale.referralPlayers}</div>
              </div>
              <SVG className={b('top-left-item-icon')} svgProps={{ svg: referralsSVG }} />
            </div>
          )}
        </div>
        <div className={b('top-right')}>
          <div className={b('filters')}>
            <Filters model={model} />
          </div>

          <div className={b('table')}>
            <LineChart
              title={data.title}
              labels={data?.time || []}
              colorFill={data?.colorFill}
              color={data?.color}
              data={data?.data || []}
            />
          </div>
        </div>
      </div>
      <div>
        <div className={b('center')}>
          <div className={b('tabs')}>
            <div onClick={() => switchTab('agents')} className={b('tab', { active: activeTab === 'agents' })}>
              {locale.myAgents}
            </div>
            <div onClick={() => switchTab('players')} className={b('tab', { active: activeTab === 'players' })}>
              {locale.myPlayers}
            </div>
          </div>
          <div className={b('center-right')}>
            <div className={b('center-right-item')}>
              <div className={b('center-right-item1')}>{locale.playerBalance}</div>
              <div className={b('center-right-item2')}>{statistic.totalUserBalance}</div>
            </div>
            <div className={b('center-right-item')}>
              <div className={b('center-right-item1')}>{locale.agentBalance}</div>
              <div className={b('center-right-item2')}>{statistic.totalAgentBalance}</div>
            </div>
          </div>
        </div>
        <div className={b('bottom')}>
          {activeTab === 'agents' && (
            <div className={b('content')}>
              <Table data={statistic.rankedAgents} type={activeTab} />
            </div>
          )}
          {activeTab === 'players' && (
            <div className={b('content')}>
              <Table data={statistic.rankedPlayers} type={activeTab} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistic;
